import { costaRicaBoletinJudicialCitation } from '#app/services/legal-ai/countries/costa-rica/sources/boletin-judicial/citation.ts'
import { costaRicaGacetaCitation } from '#app/services/legal-ai/countries/costa-rica/sources/gaceta/citation.ts'
import { SupportedCountry } from '#app/services/legal-ai/shared/types.ts'
import {
	OFFICIAL_PUBLICATION_SOURCE_IDS,
	type OfficialPublicationPayload,
	type OfficialPublicationSnapshot,
} from './types.ts'

function publicationPromptChunk(payload: OfficialPublicationPayload) {
	return `${payload.title}\nFecha de publicación: ${payload.publicationDate}\n${payload.content}`
}

function publicationMerge() {
	return {
		dedupeKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => `${payload.idDocument}:${payload.chunk_index}`,
		documentKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => payload.idDocument,
		chunkKey: (payload: Omit<OfficialPublicationPayload, 'content'>) => `${payload.idDocument}:${payload.chunk_index}`,
	}
}

export const costaRicaGacetaPublicationDefinition = {
	id: OFFICIAL_PUBLICATION_SOURCE_IDS.Gaceta,
	country: SupportedCountry.CostaRica,
	aliases: ['gaceta', 'la gaceta', 'diario oficial', 'imprenta nacional'],
	label: 'La Gaceta',
	timeZone: 'America/Costa_Rica',
	locale: 'es-CR',
	activation: 'explicit',
	retrievalKind: 'official-publication-date-range',
	citationType: 'Gaceta',
	snapshotKind: 'gaceta',
	citation: costaRicaGacetaCitation,
	prompt: {
		intro: 'Publicaciones históricas recuperadas de La Gaceta de Costa Rica.',
		buildChunk: publicationPromptChunk,
	},
	merge: publicationMerge(),
	buildTitle: (snapshot: OfficialPublicationSnapshot) => `La Gaceta del ${snapshot.date}`,
	buildDocumentId: (snapshot: OfficialPublicationSnapshot) => `cr:gaceta:${snapshot.date}:${snapshot.id}`,
} as const

export const costaRicaBoletinJudicialPublicationDefinition = {
	id: OFFICIAL_PUBLICATION_SOURCE_IDS.BoletinJudicial,
	country: SupportedCountry.CostaRica,
	aliases: ['boletin', 'boletin judicial', 'boletín', 'boletín judicial'],
	label: 'Boletín Judicial',
	timeZone: 'America/Costa_Rica',
	locale: 'es-CR',
	activation: 'explicit',
	retrievalKind: 'official-publication-date-range',
	citationType: 'BoletinJudicial',
	snapshotKind: 'boletin',
	citation: costaRicaBoletinJudicialCitation,
	prompt: {
		intro: 'Publicaciones históricas recuperadas del Boletín Judicial de Costa Rica.',
		buildChunk: publicationPromptChunk,
		responseInstructions: [
			'No atribuyas avisos o alegatos del Boletín como decisiones de un tribunal.',
		],
	},
	merge: publicationMerge(),
	buildTitle: (snapshot: OfficialPublicationSnapshot) =>
		`Boletín Judicial${snapshot.externalDocumentId ? ` ${snapshot.externalDocumentId}` : ''} del ${snapshot.date}`,
	buildDocumentId: (snapshot: OfficialPublicationSnapshot) =>
		`cr:boletin-judicial:${snapshot.externalDocumentId || snapshot.id}`,
} as const

export const officialPublicationCitationDefinitions = [
	costaRicaGacetaPublicationDefinition,
	costaRicaBoletinJudicialPublicationDefinition,
] as const
